import React, { useState, useEffect } from "react";
import { saveExamDetails, getExamDetails } from "../../api/examDetails";
import { useExam } from "../../context/ExamContext";
import {
  TextField,
  Button,
  Box,
  Paper,
  Typography,
  CircularProgress,
  Grid,
} from "@mui/material";

const AddExamDetails = () => {
  const { examData, setExamData } = useExam();
  const [form, setForm] = useState({
    test_paper_name: "",
    time_allowed: "",
    full_marks: "",
    general_instruction: "",
  });
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    const fetchExamDetails = async () => {
      try {
        const result = await getExamDetails();
        const firstItem = result?.data?.[0] || result?.[0] || null;
        if (firstItem) setExamData(firstItem);
      } catch (err) {
        console.error("Error fetching exam details:", err);
      }
    };
    fetchExamDetails();
  }, [setExamData]);

  const handleChange = (e) => {
    setForm({ ...form, [e.target.name]: e.target.value });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    const formData = new FormData();
    formData.append("test_paper_name", form.test_paper_name);
    formData.append("time_allowed", form.time_allowed);
    formData.append("full_marks", form.full_marks);
    formData.append("general_instruction", form.general_instruction);

    try {
      setLoading(true);
      const result = await saveExamDetails(formData);

      const firstItem = result?.data?.[0] || result?.[0] || null;
      if (firstItem) setExamData(firstItem);

      alert("Exam details saved successfully!");
      setForm({
        test_paper_name: "",
        time_allowed: "",
        full_marks: "",
        general_instruction: "",
      });
    } catch (error) {
      console.error("Error saving exam details:", error);
      alert("Error while saving exam details!");
    } finally {
      setLoading(false);
    }
  };

  return (
    <Paper
      sx={{
        p: 4,
        maxWidth: 1000,
        mx: "auto",
        mt: 5,
        borderRadius: 3,
        boxShadow: "0px 6px 20px rgba(0,0,0,0.08)",
        background: "linear-gradient(135deg, #ffffff, #fafafa)",
      }}
    >
      <Typography
        variant="h5"
        gutterBottom
        sx={{
          fontWeight: "bold",
          mb: 3,
          textAlign: "center",
          color: "primary.main",
          letterSpacing: 0.5,
        }}
      >
        ✨ Add Exam Details
      </Typography>

      <Box
        component="form"
        onSubmit={handleSubmit}
        noValidate
        sx={{
          display: "flex",
          flexWrap: "wrap",
          gap: 3,
          alignItems: "center",
          justifyContent: "space-between",
        }}
      >
        <TextField
          label="Test Paper Name"
          name="test_paper_name"
          value={form.test_paper_name}
          onChange={handleChange}
          required
          fullWidth
          sx={{
            flex: 1,
            "& .MuiOutlinedInput-root": {
              borderRadius: 2,
            },
          }}
        />

        <TextField
          label="Time Allowed"
          name="time_allowed"
          value={form.time_allowed}
          onChange={handleChange}
          sx={{
            flex: 0.5,
            "& .MuiOutlinedInput-root": {
              borderRadius: 2,
            },
          }}
        />

        <TextField
          label="Full Marks"
          name="full_marks"
          type="number"
          value={form.full_marks}
          onChange={handleChange}
          required
          sx={{
            flex: 0.5,
            "& .MuiOutlinedInput-root": {
              borderRadius: 2,
            },
          }}
        />

        <TextField
          label="General Instructions"
          name="general_instruction"
          value={form.general_instruction}
          onChange={handleChange}
          multiline
          rows={2}
          fullWidth
          sx={{
            flex: 1,
            "& .MuiOutlinedInput-root": {
              borderRadius: 2,
            },
          }}
        />

        <Box sx={{ textAlign: "center", flex: 1 }}>
          <Button
            type="submit"
            variant="contained"
            disabled={loading}
            startIcon={loading && <CircularProgress size={20} />}
            sx={{
              px: 3,
              py: 1.5,
              borderRadius: 3,
              fontWeight: "bold",
              textTransform: "none",
              boxShadow: "0px 4px 12px rgba(0,0,0,0.2)",
              "&:hover": {
                boxShadow: "0px 6px 16px rgba(0,0,0,0.25)",
              },
            }}
          >
            {loading ? "Saving..." : "Save Exam Details"}
          </Button>
        </Box>
      </Box>

      {examData && (
        <Paper
          sx={{
            mt: 5,
            p: 4,
            borderRadius: 3,
            bgcolor: "white",
            boxShadow: "0px 6px 18px rgba(0,0,0,0.08)",
          }}
        >
          <Typography
            variant="h6"
            fontWeight="bold"
            sx={{ mb: 3, textAlign: "center", color: "primary.main" }}
          >
            📊 Latest Exam Detail
          </Typography>

          <Grid container spacing={3}>
            <Grid item xs={12} sm={6} md={3}>
              <Paper
                sx={{
                  p: 2,
                  borderRadius: 2,
                  textAlign: "center",
                  bgcolor: "#f0f7ff",
                  border: "1px solid #e3f2fd",
                }}
              >
                <Typography variant="subtitle2" color="text.secondary">
                  Test Paper Name
                </Typography>
                <Typography fontWeight="bold">
                  {examData.test_paper_name}
                </Typography>
              </Paper>
            </Grid>

            <Grid item xs={12} sm={6} md={3}>
              <Paper
                sx={{
                  p: 2,
                  borderRadius: 2,
                  textAlign: "center",
                  bgcolor: "#fef6f0",
                  border: "1px solid #ffe0b2",
                }}
              >
                <Typography variant="subtitle2" color="text.secondary">
                  Time Allowed
                </Typography>
                <Typography fontWeight="bold">
                  {examData.time_allowed}
                </Typography>
              </Paper>
            </Grid>

            <Grid item xs={12} sm={6} md={3}>
              <Paper
                sx={{
                  p: 2,
                  borderRadius: 2,
                  textAlign: "center",
                  bgcolor: "#f3fdf6",
                  border: "1px solid #c8e6c9",
                }}
              >
                <Typography variant="subtitle2" color="text.secondary">
                  Full Marks
                </Typography>
                <Typography fontWeight="bold">{examData.full_marks}</Typography>
              </Paper>
            </Grid>

            <Grid item xs={12} md={3}>
              <Paper
                sx={{
                  p: 2,
                  borderRadius: 2,
                  textAlign: "center",
                  bgcolor: "#faf5ff",
                  border: "1px solid #e1bee7",
                }}
              >
                <Typography variant="subtitle2" color="text.secondary">
                  Instructions
                </Typography>
                <Typography
                  fontWeight="bold"
                  sx={{ whiteSpace: "pre-line", fontSize: "0.9rem" }}
                >
                  {examData.general_instruction}
                </Typography>
              </Paper>
            </Grid>
          </Grid>
        </Paper>
      )}
    </Paper>
  );
};

export default AddExamDetails;
